import React, { useEffect, useRef, useState } from 'react';
import { JcCommonProps, JcSize, JcUtils } from '../utils/JcUtils';
import { JcButton, JcButtonType } from './button/JcButton';
import { JcLoadingIndicator } from './JcLoadingIndicator';

/** Prefetch distance: trigger while the sentinel is still this far below view. */
const JC_LOAD_MORE_ROOT_MARGIN = '0px 0px 250px 0px';

export interface JcLoadMoreProps extends JcCommonProps {
    /** More items exist — the sentinel renders and can trigger. */
    readonly hasMore: boolean;
    /** A load is in flight — shows the spinner and holds further triggers. */
    readonly loading: boolean;
    readonly onLoadMore: () => void;
    /** Label for the manual button (defaults to "Load more"). */
    readonly label?: string;
}

/** The observer root: the nearest scrollable ancestor, or `null` for the viewport. */
const findScrollableAncestor = (element: HTMLElement): HTMLElement | null => {
    for (let parent = element.parentElement; parent !== null; parent = parent.parentElement) {
        const { overflowY } = getComputedStyle(parent);
        if (overflowY === 'auto' || overflowY === 'scroll') {
            return parent;
        }
    }
    return null;
};

/**
 * Auto-load sentinel for the bottom of a paged list. Place it after the items;
 * it calls `onLoadMore` whenever it nears the visible area (prefetching one
 * page ahead) and re-arms after each page lands, so it keeps filling until it
 * leaves view. The visible "Load more" button is the keyboard/assistive-tech
 * affordance — on screen it usually auto-triggers before it can be pressed.
 * Renders nothing once `hasMore` is false. Pair with `useJcPagedSnapshot`'s
 * `JcPagedControls`, or drive it from local state for render-level reveal.
 */
export const JcLoadMore = (props: JcLoadMoreProps): React.ReactElement | null => {
    const { hasMore, loading, onLoadMore, label } = props;
    const active = hasMore || loading;
    const ref = useRef<HTMLDivElement>(null);
    const [intersecting, setIntersecting] = useState(false);
    const onLoadMoreRef = useRef(onLoadMore);
    onLoadMoreRef.current = onLoadMore;

    useEffect(() => {
        const sentinel = ref.current;
        if (!active || sentinel === null) {
            setIntersecting(false);
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => entries.forEach((entry) => setIntersecting(entry.isIntersecting)),
            { root: findScrollableAncestor(sentinel), rootMargin: JC_LOAD_MORE_ROOT_MARGIN },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [active]);

    useEffect(() => {
        if (intersecting && hasMore && !loading) {
            onLoadMoreRef.current();
        }
    }, [intersecting, hasMore, loading]);

    if (!active) {
        return null;
    }
    return (
        <div {...JcUtils.commonProps(props, ['jc-load-more'])} ref={ref}>
            {loading
                ? <JcLoadingIndicator size={JcSize.SMALL} />
                : <JcButton buttonType={JcButtonType.GHOST} onClick={onLoadMore}>{label ?? 'Load more'}</JcButton>}
        </div>
    );
};
